import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import Pagination from '../../components/Pagination/Pagination';
import { useMonthlyReports } from '../../hooks/useMonthlyReports';
import { useSaveMonthlyReport } from '../../hooks/useSaveMonthlyReport';
import { useSendMonthlyReports } from '../../hooks/useSendMonthlyReports';
import { sortReports } from '../../utils/sortReports';
import {
  AIResponseTextarea,
  ActionContainer,
  ButtonsContainer,
  CheckIcon,
  CheckboxContainer,
  Container,
  DateText,
  FilterButton,
  FilterContainer,
  Header,
  NoDataMessage,
  SaveButton,
  SendAllButton,
  StatusBadge,
  StyledCheckbox,
  Table,
  TableHeader,
  TableRow,
  Title,
} from './MonthlyReports.Styles';
import { useBulkSaveMonthlyReports } from '../../hooks/useSaveMonthlyReports';
// import { useSaveMonthlyReports } from '../../hooks/useSaveMonthlyReports';

/**
 * @module MonthlyReports
 * @description Component for managing monthly AI-generated reports in the Mental Health Portal application.
 * Allows users to view, filter, edit, save, and send monthly reports.
 *
 * @requires react
 * @requires react-i18next
 * @requires react-oidc-context
 * @requires ../../components/Pagination/Pagination
 * @requires ../../hooks/useMonthlyReports
 * @requires ../../hooks/useSaveMonthlyReport
 * @requires ../../hooks/useSendMonthlyReports
 * @requires ../../utils/sortReports
 * @requires ./MonthlyReports.Styles
 */
const MonthlyReports = () => {
  /** Translation hook for internationalization */
  const { t } = useTranslation();

  /**
   * @state {Array<Object>} reports - List of monthly reports with their data and UI state
   * @property {string} id - Report ID
   * @property {string} date - Formatted date string (month and year)
   * @property {Date} rawDate - JavaScript Date object for sorting
   * @property {string} aiReport - AI-generated report content
   * @property {boolean} reviewed - Whether the report has been reviewed
   * @property {boolean} originalEdited - Whether the report was already edited when loaded
   */
  const [reports, setReports] = useState([]);

  /**
   * @state {string} filter - Current filter for reports ('all', 'reviewed', or 'notReviewed')
   */
  const [filter, setFilter] = useState('all');

  /**
   * @state {number} page - Current page number for pagination
   */
  const [page, setPage] = useState(1);

  /**
   * @const {number} pageSize - Number of reports to display per page
   */
  const pageSize = 20;

  /**
   * Filters reports based on current filter state
   * @function getFilteredReports
   * @returns {Array} Filtered reports array
   */
  const getFilteredReports = () => {
    if (filter === 'reviewed') return reports.filter((r) => r.reviewed);
    if (filter === 'notReviewed') return reports.filter((r) => !r.reviewed);
    return reports;
  };

  /**
   * Custom hook for fetching monthly reports based on filters and pagination
   * @type {Object}
   * @property {Object} data - Response data containing reports and total count
   * @property {boolean} loading - Loading state for reports
   * @property {string|null} error - Error message if fetch fails
   */
  const { data, loading, error } = useMonthlyReports({
    all: true,
    page,
    pageSize,
  });

  /** Custom hook for saving edited monthly reports */
  const { saveMonthlyReport } = useSaveMonthlyReport();

  /** Custom hook for saving multiple edited monthly reports */
  const { saveMonthlyReports } = useBulkSaveMonthlyReports();

  /** Custom hook for sending reviewed reports */
  const { sendReports } = useSendMonthlyReports();

  /** Authentication hook for user management */
  const auth = useAuth();

  /** User ID from authentication */
  const userId = auth.user?.profile?.sub;

  /** Total number of pages for pagination */
  const totalPages = Math.ceil((data?.total || 0) / pageSize);

  /**
   * Updates a report with new fields and moves it to the top of the list
   * @function moveReportToTop
   * @param {string} id - ID of the report to update
   * @param {Object} updatedFields - New fields to add to the report
   * @returns {void}
   */
  const moveReportToTop = (id, updatedFields = {}) => {
    setReports((prev) => {
      const updatedReports = prev.map((r) =>
        r.id === id ? { ...r, ...updatedFields } : r
      );

      return sortReports(updatedReports);
    });
  };

  /**
   * Effect hook to format and sort reports when data is loaded from API
   * @effect
   * @param {Object} data - The reports data from API
   * @returns {void}
   */
  useEffect(() => {
    if (!data?.reports) return;

    // Transform API report format to component state format
    const formattedReports = data.reports.map((report) => ({
      id: report.id,
      date: new Date(report.date).toLocaleDateString('pt-BR', {
        month: 'long',
        year: 'numeric',
      }),
      rawDate: new Date(report.date),
      aiReport: report.aiReport,
      reviewed: report.edited,
      originalEdited: report.edited,
    }));

    // Sort reports and update state
    setReports(sortReports(formattedReports));
  }, [data]);

  /**
   * Updates the AI report text and marks it as reviewed
   * @function handleAIResponseChange
   * @param {string} id - ID of the report to update
   * @param {string} newValue - New AI report text
   * @returns {void}
   */
  const handleAIResponseChange = (id, newValue) => {
    setReports((prev) =>
      prev.map((report) =>
        report.id === id
          ? { ...report, aiReport: newValue, reviewed: true }
          : report
      )
    );
  };

  /**
   * Toggles the reviewed status of a report
   * @function toggleReviewed
   * @param {string} id - ID of the report to toggle
   * @returns {void}
   */
  const toggleReviewed = (id) => {
    setReports((prev) =>
      prev.map((report) =>
        report.id === id ? { ...report, reviewed: !report.reviewed } : report
      )
    );
  };

  /**
   * Saves an edited report to the database
   * @function saveReport
   * @async
   * @param {string} id - ID of the report to save
   * @returns {Promise<void>}
   * @throws {Error} If saving fails
   */
  const saveReport = async (id) => {
    const reportToSave = reports.find((r) => r.id === id);

    if (!reportToSave) {
      alert(`${t('Report not found')}`);
      return;
    }

    try {
      // Save report to database
      await saveMonthlyReport({
        id,
        newAiReport: reportToSave.aiReport,
        userId: userId,
      });

      // Show success message and update UI
      alert(`${t('Report')} ${id} ${t('saved successfully')}`);
      moveReportToTop(id, { saved: true });
    } catch (error) {
      console.error('Erro ao salvar o relatório mensal:', error);
      alert(`${t('Error saving report')}`);
    }
  };

  /**
   * Sends all reviewed reports
   * @function sendAllReviewed
   * @async
   * @returns {Promise<void>}
   * @throws {Error} If sending fails
   */
  const sendAllReviewed = async () => {
    const reviewedReports = reports.filter((report) => report.reviewed);

    if (reviewedReports.length === 0) {
      alert(`${t('No revised report to send')}`);
      return;
    }

    try {
      // Send all reviewed reports
      await sendReports();

      // Show success message and reset reviewed status
      alert(`${reviewedReports.length} ${t('reports sent successfully')}`);
      setReports((prev) => prev.map((r) => ({ ...r, reviewed: false })));
    } catch (err) {
      console.error('Erro ao enviar relatórios mensais:', err);
      alert(`${t('Failed to send reports')}`);
    }
  };

  const saveAllReviewed = async () => {
    const reviewedReports = reports.filter((r) => r.reviewed);

    if (reviewedReports.length === 0) {
      alert(`${t('No revised report to save')}`);
      return;
    }

    try {
      await saveMonthlyReports(reviewedReports);

      alert(`${reviewedReports.length} ${t('reports saved successfully')}`);

      // Optional: update local state/UI
      // setReports((prev) =>
      //   prev.map((r) =>
      //     r.reviewed ? { ...r, sent: true, reviewed: false } : r
      //   )
      // );
    } catch (err) {
      alert(`${t('Failed to save reports')}`);
    }
  };

  return (
    <Container>
      <Header>
        <Title>{t('monthlyReport.title')}</Title>
        <ButtonsContainer>
          <SendAllButton
            onClick={saveAllReviewed}
            disabled={reports.filter((r) => r.reviewed).length === 0}
          >
            {t('Save revised reports')}
          </SendAllButton>
          <SendAllButton
            onClick={sendAllReviewed}
            disabled={reports.filter((r) => r.reviewed).length === 0}
          >
            {t('Submit revised reports')}
          </SendAllButton>
        </ButtonsContainer>
      </Header>

      <FilterContainer>
        <FilterButton
          active={filter === 'all'}
          onClick={() => {
            setFilter('all');
            setPage(1);
          }}
        >
          {t('All filter')}
        </FilterButton>
        <FilterButton
          active={filter === 'reviewed'}
          onClick={() => {
            setFilter('reviewed');
            setPage(1);
          }}
        >
          {t('Reviewed filter')}
        </FilterButton>
        <FilterButton
          active={filter === 'notReviewed'}
          onClick={() => {
            setFilter('notReviewed');
            setPage(1);
          }}
        >
          {t('Not Reviewed filter')}
        </FilterButton>
      </FilterContainer>

      {loading ? (
        <NoDataMessage>{t('Loading Reports')}</NoDataMessage>
      ) : error ? (
        <NoDataMessage>{t('Error loading reports')}</NoDataMessage>
      ) : getFilteredReports().length === 0 ? (
        <NoDataMessage>{t('No Reports Found')}</NoDataMessage>
      ) : (
        <Table>
          <TableHeader>
            <div>{t('AI Report')}</div>
            <div>{t('Actions')}</div>
          </TableHeader>

          {getFilteredReports().map((report) => (
            <TableRow key={report.id}>
              <div>
                <DateText>{report.date}</DateText>
                {report.originalEdited && (
                  <StatusBadge edited>{t('Edited')}</StatusBadge>
                )}
                <AIResponseTextarea
                  value={report.aiReport}
                  onChange={(e) =>
                    handleAIResponseChange(report.id, e.target.value)
                  }
                  placeholder={t('AI response field')}
                />
              </div>

              <ActionContainer>
                <CheckboxContainer>
                  <StyledCheckbox
                    checked={report.reviewed}
                    onClick={() => toggleReviewed(report.id)}
                  >
                    {report.reviewed && <CheckIcon>✓</CheckIcon>}
                  </StyledCheckbox>
                </CheckboxContainer>
                <SaveButton onClick={() => saveReport(report.id)}>
                  {t('Save Button')}
                </SaveButton>
              </ActionContainer>
            </TableRow>
          ))}
        </Table>
      )}

      {getFilteredReports().length > 0 && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      )}
    </Container>
  );
};

export default MonthlyReports;
